import { Button, Grid, Stack, TextField, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import BatteryTrackingSelect from "../../components/device/selects/battery_tracking_select";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MeteringDirectionSelect from "../../components/device/selects/metering_direction_select";
import HelpedTextField from "../../components/general/helped_text_field";
import { DeviceClass, Protocol } from "../../model/device";
import { isComponentDisabledForUpdates } from "../../pages/login/login_utils";
import PhaseConfigSelect from "../../components/device/selects/phase_config_select";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import { fetchSiteProperties } from "../../scripts/api_v2/properties_client";
import { useTheme } from "@emotion/react";
import { getSiteGreenPowerCertificates } from "../../scripts/api_v2/gpcs_client";
import BatteryTypeSelect from "./selects/battery_type_select";
import Snackbar from "../../scripts/snackbar";

const configGridItemSx = { display: "flex", alignItems: "center", justifyContent: "left", px: 2, py: 1 };

function isInternetProtocol(protocol) {
  return protocol === Protocol.TCP || protocol === Protocol.TCP_RTU;
}

function isHite(device_type) {
  try {
    return device_type.toLowerCase().includes("hite");
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// SOC helpers (device-level min/max SOC comes from site properties and
// defines the true editable range; configured values outside that range are
// clamped by the backend, so we preview the clamp here).
// ---------------------------------------------------------------------------

function isSocOutOfRange(configuredValue, minAllowed, maxAllowed) {
  if (configuredValue === null || configuredValue === undefined) return false;
  const trimmed = configuredValue.toString().trim();
  if (trimmed === "") return false;

  const parsed = parseInt(trimmed);
  if (isNaN(parsed)) return false;

  return parsed < minAllowed || parsed > maxAllowed;
}

function getPreviewAppliedSoc(configuredValue, backendAppliedValue, minAllowed, maxAllowed) {
  let configured = null;
  if (configuredValue !== null && configuredValue !== undefined) {
    const configuredTrimmed = configuredValue.toString().trim();
    if (configuredTrimmed !== "") {
      const configuredParsed = parseInt(configuredTrimmed);
      configured = isNaN(configuredParsed) ? null : configuredParsed;
    }
  }

  if (configured === null) {
    let backendApplied = null;
    if (backendAppliedValue !== null && backendAppliedValue !== undefined) {
      const backendTrimmed = backendAppliedValue.toString().trim();
      if (backendTrimmed !== "") {
        const backendParsed = parseInt(backendTrimmed);
        backendApplied = isNaN(backendParsed) ? null : backendParsed;
      }
    }
    return backendApplied === null ? "-" : backendApplied;
  }

  if (configured < minAllowed) return minAllowed;
  if (configured > maxAllowed) return maxAllowed;
  return configured;
}

function SocValuePreview({ configuredValue, previewAppliedValue, minAllowed, maxAllowed, theme }) {
  let configuredNumber = null;
  let hasConfiguredValue = false;
  if (configuredValue !== null && configuredValue !== undefined) {
    const configuredTrimmed = configuredValue.toString().trim();
    if (configuredTrimmed !== "") {
      const configuredParsed = parseInt(configuredTrimmed);
      configuredNumber = isNaN(configuredParsed) ? null : configuredParsed;
      hasConfiguredValue = !isNaN(configuredParsed);
    }
  }

  const isConfiguredInvalid = isSocOutOfRange(configuredValue, minAllowed, maxAllowed);

  // When configured SOC is missing, applied value is shown in helperText for a cleaner single-row layout.
  if (!hasConfiguredValue) return null;

  if (!isConfiguredInvalid) return null;

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "flex-start", md: "center" }}
      sx={{
        mt: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: "1px solid",
        borderColor: theme.palette.primary.main,
        backgroundColor: "#ff1a3d1a",
        flexWrap: "wrap",
        gap: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
        Configured → Applied:
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            textDecoration: "line-through",
            whiteSpace: "nowrap",
          }}
        >
          {configuredNumber === null ? "-" : `${configuredNumber}%`}
        </Typography>

        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
          →
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            lineHeight: 1,
            color: theme.palette.primary.main,
            whiteSpace: "nowrap",
          }}
        >
          {previewAppliedValue === "-" ? "-" : `${previewAppliedValue}%`}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.primary.main, whiteSpace: "nowrap" }}>
          (Device range: {minAllowed}% - {maxAllowed}%)
        </Typography>
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Device-type-specific extra fields (Gain for A1-Dongle & A1-Xemex).
//
// Configuration format:
// - field: backend field name
// - label: UI label displayed to user
// - info: tooltip/help text
// - type: "float" | "int" | "string"
// - validate: (optional) validation function(value) returns null if valid or error message if invalid
//
// To add a new device type or field, simply add an entry to this object.
// ---------------------------------------------------------------------------

const validatePositiveNumber = (value, fieldName) => {
  if (value === "" || value === undefined || value === null) return null;
  const num = parseFloat(value);
  return isNaN(num) || num < 0 ? `${fieldName} must be a positive number` : null;
};

const DEVICE_TYPE_EXTRA_FIELDS = {
  "A1-Dongle": [
    {
      field: "current_power_gain",
      label: "Gain (TI*TP)",
      info: "Power gain applied for this device",
      type: "float",
      validate: (value) => validatePositiveNumber(value, "Gain"),
    },
  ],
  "A1-Xemex": [
    {
      field: "power_gain",
      label: "Gain (TI*TP)",
      info: "Power gain applied for this device",
      type: "float",
      validate: (value) => validatePositiveNumber(value, "Gain"),
    },
  ],
};

function parseFieldValue(value, type) {
  if (value === "" || value === undefined || value === null) return type === "string" ? "" : 0;
  if (type === "float") return parseFloat(value);
  if (type === "int") return parseInt(value);
  return value.toString();
}

function validateDeviceFields(device) {
  const fields = DEVICE_TYPE_EXTRA_FIELDS[device?.device_type];
  if (!fields?.length) return null;

  for (const field of fields) {
    if (field.validate) {
      const error = field.validate(device[field.field]);
      if (error) return error;
    }
  }
  return null;
}

function DeviceTypeSpecs({ device, handleChange }) {
  const fields = DEVICE_TYPE_EXTRA_FIELDS[device?.device_type];
  if (!fields?.length) return null;

  const handleFieldChange = (field, value) => {
    const fieldConfig = DEVICE_TYPE_EXTRA_FIELDS[device.device_type].find((f) => f.field === field);
    const parsedValue = parseFieldValue(value, fieldConfig?.type);
    handleChange(field, parsedValue, false);
  };

  return fields.map(({ field, label, info, type }) => (
    <Grid
      key={field}
      md={6}
      xs={12}
      item
      width={"80%"}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      px={2}
      py={2}
    >
      <HelpedTextField
        info={info}
        label={label}
        fullWidth
        type={type === "string" ? "text" : "number"}
        value={device[field] ?? ""}
        onChange={(e) => handleFieldChange(field, e.target.value)}
      />
    </Grid>
  ));
}

function BatterySpecs({
  device,
  handleChange,
  handleSocCommit,
  actualMinimumSoc,
  actualMaximumSoc,
  deviceMinimumSoc,
  deviceMaximumSoc,
}) {
  const theme = useTheme();

  if (device?.device_class === DeviceClass.BATTERY) {
    // Device-level SOC limits come from site properties and define the true editable range.
    const parsedMinAllowed = Number(deviceMinimumSoc);
    const parsedMaxAllowed = Number(deviceMaximumSoc);
    const minAllowed = Number.isFinite(parsedMinAllowed) ? parsedMinAllowed : 0;
    const maxAllowed = Number.isFinite(parsedMaxAllowed) ? parsedMaxAllowed : 100;
    const isMinOutOfRange = isSocOutOfRange(device.minimum_soc, minAllowed, maxAllowed);
    const isMaxOutOfRange = isSocOutOfRange(device.maximum_soc, minAllowed, maxAllowed);
    const minPreviewApplied = getPreviewAppliedSoc(device.minimum_soc, actualMinimumSoc, minAllowed, maxAllowed);
    const maxPreviewApplied = getPreviewAppliedSoc(device.maximum_soc, actualMaximumSoc, minAllowed, maxAllowed);
    const minConfiguredMissing =
      device.minimum_soc === null || device.minimum_soc === undefined || device.minimum_soc.toString().trim() === "";
    const maxConfiguredMissing =
      device.maximum_soc === null || device.maximum_soc === undefined || device.maximum_soc.toString().trim() === "";

    return (
      <>
        {isHite(device.device_type) && (
          <Grid
            md={6}
            xs={12}
            item
            width={"80%"}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            px={2}
            py={2}
          >
            <BatteryTypeSelect
              key="battype"
              fullWidth
              showAll
              value={device.device_type}
              onChange={(e) => handleChange("device_type", e.target.value, true)}
            />
          </Grid>
        )}
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <HelpedTextField
            info="Total capacity of the battery"
            key="capacity"
            label={"Capacity (Wh)"}
            fullWidth
            value={device.capacity}
            onChange={(e) => handleChange("capacity", e.target.value, false)}
          />
        </Grid>
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <HelpedTextField
            info="Maximum charge rate of the battery"
            key="chargerate"
            label={"Charge rate (W)"}
            fullWidth
            value={device.charge_rate}
            onChange={(e) => handleChange("charge_rate", e.target.value, false)}
          />
        </Grid>
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <Stack direction="column" width="100%">
            <HelpedTextField
              info={`Minimum SOC (Device range: ${minAllowed}% - ${maxAllowed}%)`}
              key="minSoc"
              label={"Minimum SOC (%)"}
              fullWidth
              type="number"
              inputProps={{ min: minAllowed, max: maxAllowed }}
              value={device.minimum_soc ?? ""}
              onChange={(e) => handleChange("minimum_soc", e.target.value, false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSocCommit("minimum_soc", e.target.value);
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => handleSocCommit("minimum_soc", e.target.value)}
              helperText={
                isMinOutOfRange
                  ? ""
                  : minConfiguredMissing && minPreviewApplied !== "-"
                    ? `Device range: ${minAllowed}% - ${maxAllowed}% | Applied: ${minPreviewApplied}%`
                    : `Device range: ${minAllowed}% - ${maxAllowed}%`
              }
            />
            <SocValuePreview
              // When configured SOC is invalid, show how the backend effectively applies a clamped value.
              configuredValue={device.minimum_soc}
              previewAppliedValue={minPreviewApplied}
              minAllowed={minAllowed}
              maxAllowed={maxAllowed}
              theme={theme}
            />
          </Stack>
        </Grid>
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <Stack direction="column" width="100%">
            <HelpedTextField
              info={`Maximum SOC (Device range: ${minAllowed}% - ${maxAllowed}%)`}
              key="maxSoc"
              label={"Maximum SOC (%)"}
              fullWidth
              type="number"
              inputProps={{ min: minAllowed, max: maxAllowed }}
              value={device.maximum_soc ?? ""}
              onChange={(e) => handleChange("maximum_soc", e.target.value, false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSocCommit("maximum_soc", e.target.value);
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => handleSocCommit("maximum_soc", e.target.value)}
              helperText={
                isMaxOutOfRange
                  ? ""
                  : maxConfiguredMissing && maxPreviewApplied !== "-"
                    ? `Device range: ${minAllowed}% - ${maxAllowed}% | Applied: ${maxPreviewApplied}%`
                    : `Device range: ${minAllowed}% - ${maxAllowed}%`
              }
            />
            <SocValuePreview
              // Same mismatch preview behavior for maximum SOC.
              configuredValue={device.maximum_soc}
              previewAppliedValue={maxPreviewApplied}
              minAllowed={minAllowed}
              maxAllowed={maxAllowed}
              theme={theme}
            />
          </Stack>
        </Grid>
      </>
    );
  }
}

function InverterSpecs({ device, handleChange, gpc, loadingGPC, handleGPCChange }) {
  if (device?.device_class === DeviceClass.SOLAR_INVERTER) {
    return (
      <>
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <HelpedTextField
            info="Maximum inverter power"
            key="chargerate"
            label={"Inverter power (W)"}
            fullWidth
            value={device.inverter_power}
            onChange={(e) => handleChange("inverter_power", e.target.value, false)}
          />
        </Grid>
        {/* <Grid md={6} xs={12} item width={"80%"}
                  sx={{display: "flex", alignItems: "center", justifyContent: "center"}} px={2} py={2}>
                {loadingGPC ? <Loader/> : <HelpedTextField info="Maximum inverter power" key="gpc" label={"Green power certificate (€)"} fullWidth
                                 value={gpc}
                                 onChange={(e) => handleGPCChange(e.target.value)}/>}
            </Grid> */}
      </>
    );
  }
}

function InfoLine({ name, value }) {
  const theme = useTheme();
  return (
    <Grid md={6} xs={12} item sx={configGridItemSx}>
      <Typography variant="h7" sx={{ display: "block", width: "100%" }}>
        <b
          style={{
            color: theme.palette.text.primary_bold,
            width: "160px",
            paddingRight: 10,
            display: "inline-block",
            textAlign: "left",
          }}
        >
          {name}:
        </b>
        <b style={{ color: "#999", display: "inline-block", paddingLeft: 10 }}>{value}</b>
      </Typography>
    </Grid>
  );
}

function CheckBoxes({ device, handleChange }) {
  if (device?.device_class === DeviceClass.BATTERY) {
    return (
      <Stack alignItems="center" direction="row" spacing={1}>
        <CheckBox
          label="Read"
          info="The device will be read when checked, default enabled when write is checked"
          checked={device.enabled}
          setChecked={(v) => {
            handleChange("enabled", v, false);
          }}
        />
        <CheckBox
          info="The device will be controlled when checked"
          disabled={!device.enabled}
          label="Control"
          checked={device.battery_tracking_enabled}
          setChecked={(v) => handleChange("battery_tracking_enabled", v, false)}
        />
        {device.curtailment_enabled !== null && (
          <CheckBox
            info="Production will be controlled when enabled"
            disabled={!device.enabled}
            label="Curtail"
            checked={device.curtailment_enabled}
            setChecked={(v) => handleChange("curtailment_enabled", v, false)}
          />
        )}
      </Stack>
    );
  } else if (device?.device_class === DeviceClass.METER) {
    return (
      <CheckBox
        label="Read"
        checked={device.enabled}
        setChecked={(v) => {
          handleChange("enabled", v, false);
        }}
      />
    );
  } else if (device?.device_class === DeviceClass.SOLAR_INVERTER) {
    return (
      <Stack alignItems="center" direction="row" spacing={2}>
        <CheckBox
          label="Read"
          checked={device.enabled}
          setChecked={(v) => {
            handleChange("enabled", v, false);
          }}
        />
        <CheckBox
          info="The device will be controlled when checked"
          disabled={!device.enabled}
          label="Write"
          checked={device.curtailment_enabled}
          setChecked={(v) => handleChange("curtailment_enabled", v, false)}
        />
      </Stack>
    );
  }
}

function DeviceModes({ device, handleChange }) {
  if (device?.device_class === DeviceClass.BATTERY) {
    return (
      <>
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <BatteryTrackingSelect
            key="batterytrack"
            batteryType={device.device_type}
            value={device.battery_tracking_mode}
            onChange={(e) => handleChange("battery_tracking_mode", e.target.value, false)}
          />
        </Grid>
      </>
    );
  } else if (device?.device_class === DeviceClass.METER) {
    return (
      <>
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <MeteringDirectionSelect
            key="direction"
            value={device.direction}
            onChange={(e) => handleChange("direction", e.target.value, false)}
          />
        </Grid>
      </>
    );
  }
}

export function EditDevice({ device, handleSaveDevice, handleClose, permissionMap, required_privilege }) {
  const [deviceEdits, setDeviceEdits] = React.useState({});
  const [deviceEditState, setDeviceEditState] = React.useState(device);
  const params = useParams();
  const backend = useBackend();

  let { data: siteData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  let { data: siteProps } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PROPERTIES, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProperties(backend.fetch, params.site_id),
  });

  const { data: gpcs, loadingGPC } = useQuery({
    queryKey: [REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id],
    queryFn: () => getSiteGreenPowerCertificates(backend.fetch, params.site_id),
    refetchInterval: 30000,
  });

  const [GPCEditState, setGPCEditState] = React.useState(gpcs ? gpcs[device.device_id]?.price : null);

  React.useEffect(() => {
    setDeviceEditState(device);
    setGPCEditState(gpcs ? gpcs[device.device_id]?.price : null);
  }, [device, gpcs]);

  function getDeviceProperty(key) {
    try {
      return siteProps?.device_properties?.[device.device_id]?.[key];
    } catch (e) {
      return null;
    }
  }

  const actualMinimumSoc = getDeviceProperty("minimum_soc");
  const actualMaximumSoc = getDeviceProperty("maximum_soc");
  const deviceMinimumSoc = getDeviceProperty("device_minimum_soc");
  const deviceMaximumSoc = getDeviceProperty("device_maximum_soc");

  // trackEdit lets callers update the UI state without registering the
  // field as a pending edit to send.
  function handleDeviceChange(field, value, isString = false, trackEdit = true) {
    const isSocField = field === "minimum_soc" || field === "maximum_soc";
    const isEmptySocValue = isSocField && typeof value === "string" && value.trim() === "";
    const processed = isString
      ? value.toString()
      : isEmptySocValue
        ? null
        : typeof value == "boolean" || typeof value == "number"
          ? value
          : value.length > 0
            ? parseInt(value)
            : 0;

    if (trackEdit) {
      setDeviceEdits((prev) => {
        const updated = { ...prev };
        updated[field] = {
          device_id: deviceEditState.device_id,
          field: field,
          value: processed,
          prev_value: device[field],
        };
        return updated;
      });
    }

    // Always update UI state so user sees changes in real-time
    setDeviceEditState((prev) => {
      const updated = { ...prev };
      updated[field] = isString ? value.toString() : value;
      return updated;
    });
  }

  function normalizeSocValue(rawValue) {
    if (rawValue === null || rawValue === undefined) return null;
    const trimmed = rawValue.toString().trim();
    if (trimmed === "") return null;

    const parsed = parseInt(trimmed);
    const min = deviceMinimumSoc ?? 0;
    const max = deviceMaximumSoc ?? 100;

    if (isNaN(parsed) || parsed < min) return min;
    if (parsed > max) return max;
    return parsed;
  }

  function handleSocCommit(field, rawValue) {
    const normalizedValue = normalizeSocValue(rawValue);

    setDeviceEdits((prev) => {
      const updated = { ...prev };
      updated[field] = {
        device_id: deviceEditState.device_id,
        field: field,
        value: normalizedValue,
        prev_value: device[field],
      };

      return updated;
    });

    setDeviceEditState((prev) => {
      const updated = { ...prev };
      updated[field] = normalizedValue;
      return updated;
    });
  }

  function handleGPCChange(value) {
    setDeviceEdits((prev) => {
      const updated = { ...prev };
      updated["green_power_certificate"] = {
        device_id: deviceEditState.device_id,
        field: "green_power_certificate",
        value: value,
        prev_value: gpcs ? gpcs[deviceEditState.device_id]?.price : null,
      };
      return updated;
    });
    setGPCEditState(value);
  }

  if (!siteData || !device) return;

  return (
    <>
      <Grid container justifyContent="start">
        <InfoLine name={"Device ID"} value={deviceEditState.device_id} />
        <InfoLine name={"Serial number"} value={deviceEditState.serial_number ? deviceEditState.serial_number : "-"} />
        <InfoLine
          name={"MAC address"}
          value={isInternetProtocol(deviceEditState.protocol) ? deviceEditState.mac_address : "-"}
        />
        <InfoLine name={"Number of phases"} value={deviceEditState.nb_phases ? deviceEditState.nb_phases : "Unknown"} />
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
          pt={5}
        >
          <TextField
            key="name"
            label={"Name"}
            fullWidth
            value={deviceEditState.name}
            onChange={(e) => handleDeviceChange("name", e.target.value, true)}
          />
        </Grid>

        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "left" }}
          px={2}
          py={2}
          pt={5}
        >
          <CheckBoxes device={deviceEditState} handleChange={handleDeviceChange} />
        </Grid>

        <DeviceModes device={deviceEditState} handleChange={handleDeviceChange} />
        <Grid
          md={6}
          xs={12}
          item
          width={"80%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          px={2}
          py={2}
        >
          <PhaseConfigSelect
            key="phaseconfig"
            label={"Phase configuration"}
            fullWidth
            value={deviceEditState.phase_configuration}
            onChange={(e) => handleDeviceChange("phase_configuration", e.target.value, false)}
          />
        </Grid>
        <DeviceTypeSpecs device={deviceEditState} handleChange={handleDeviceChange} />
        <BatterySpecs
          device={deviceEditState}
          handleChange={handleDeviceChange}
          handleSocCommit={handleSocCommit}
          actualMinimumSoc={actualMinimumSoc}
          actualMaximumSoc={actualMaximumSoc}
          deviceMinimumSoc={deviceMinimumSoc}
          deviceMaximumSoc={deviceMaximumSoc}
        />
        <InverterSpecs
          device={deviceEditState}
          handleChange={handleDeviceChange}
          gpc={GPCEditState}
          handleGPCChange={handleGPCChange}
          loadingGPC={loadingGPC}
        />
        <Grid
          md={12}
          xs={12}
          item
          width={"100%"}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "auto" }}
          px={2}
          py={2}
        >
          <Button
            onClick={() => {
              handleClose();
            }}
            sx={{ mr: 5 }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (deviceEditState?.device_class === DeviceClass.BATTERY) {
                const deviceMin = deviceMinimumSoc ?? 0;
                const deviceMax = deviceMaximumSoc ?? 100;
                const minSocEmpty =
                  deviceEditState.minimum_soc === null ||
                  deviceEditState.minimum_soc === undefined ||
                  deviceEditState.minimum_soc.toString().trim() === "";
                const maxSocEmpty =
                  deviceEditState.maximum_soc === null ||
                  deviceEditState.maximum_soc === undefined ||
                  deviceEditState.maximum_soc.toString().trim() === "";
                const minSoc = parseInt(deviceEditState.minimum_soc);
                const maxSoc = parseInt(deviceEditState.maximum_soc);

                if (!minSocEmpty && (isNaN(minSoc) || minSoc < deviceMin || minSoc > deviceMax)) {
                  Snackbar.error(`Minimum SOC must be between ${deviceMin}% and ${deviceMax}%`);
                  return;
                }
                if (!maxSocEmpty && (isNaN(maxSoc) || maxSoc < deviceMin || maxSoc > deviceMax)) {
                  Snackbar.error(`Maximum SOC must be between ${deviceMin}% and ${deviceMax}%`);
                  return;
                }
                if (!minSocEmpty && !maxSocEmpty && minSoc > maxSoc) {
                  Snackbar.error("Minimum SOC cannot be greater than Maximum SOC");
                  return;
                }
              }
              // Validate device-type specific fields (e.g. TI/TP/Gain)
              const fieldError = validateDeviceFields(deviceEditState);
              if (fieldError) {
                Snackbar.error(fieldError);
                return;
              }
              handleSaveDevice(deviceEdits, deviceEditState);
              handleClose();
            }}
            autoFocus
            disabled={isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey())}
          >
            Save changes
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export function CheckBox({ label, info, checked, setChecked, disabled }) {
  return (
    <Tooltip placement="top" title={info}>
      <Button
        disabled={disabled}
        onClick={() => setChecked(!checked)}
        sx={{ height: 56 }}
        variant="outlined"
        color="success"
        size="large"
        startIcon={checked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      >
        {label}
      </Button>
    </Tooltip>
  );
}